import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { getGraphQLStyleErrorMessage } from '@jepto/shared/utils/graphql-style-error-message.util';
import { isChunkLoadFailure } from './chunk-load-error.util';
import { JepErrorHandlerService } from './jep-error-handler.service';

/**
 * Chained ErrorHandler that forwards errors to both Sentry and JepErrorHandlerService.
 * Sentry captures the exception for remote monitoring, while JepErrorHandlerService
 * handles chunk-load reload, Sentry logging, and other app-specific error logic.
 */
@Injectable()
export class JepChainedErrorHandler implements ErrorHandler {
	constructor(private jepErrorHandler: JepErrorHandlerService) {}

	handleError(error: any): void {
		const raw = error?.originalError || error;
		if (isChunkLoadFailure(raw) || isChunkLoadFailure(error)) {
			this.jepErrorHandler.handleError(error);
			return;
		}

		// 1. Send to Sentry for remote error tracking (normalize GraphQL payloads first)
		try {
			const toCapture = this.normalizeForSentry(raw);
			if (toCapture) {
				Sentry.captureException(toCapture);
			}
		} catch (sentryError) {
			console.error('Sentry captureException failed:', sentryError);
		}

		// 2. Delegate to app-specific error handler (chunk reload, logging, etc.)
		this.jepErrorHandler.handleError(error);
	}

	/**
	 * Normalize errors before Sentry. GraphQL responses { data, errors } are plain
	 * objects that Sentry rejects; convert them to proper Error instances.
	 */
	private normalizeForSentry(err: any): Error | null {
		if (!err) return null;

		if (err instanceof Error) {
			return err;
		}

		const gqlMessage = getGraphQLStyleErrorMessage(err);
		if (gqlMessage !== null) {
			return new Error(gqlMessage);
		}

		// Fallback for other plain objects
		if (typeof err === 'object') {
			return new Error(
				(err as { message?: string }).message || JSON.stringify(err)
			);
		}

		return new Error(String(err));
	}
}
